import { t, useLanguage } from "@/i18n/LanguageContext";import { useState } from "react";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import AiButton from "@/pages/admin/AiButton";

const CATEGORIES = [
"Edilizia scolastica",
"Edilizia sanitaria",
"Edilizia sociale e residenziale",
"Riqualificazione urbana",
"Consolidamento geotecnico",
"Manutenzione stradale",
"Linee Media & Bassa Tensione"];


const EMPTY = { title: "", category: CATEGORIES[0], location: "", year: "", status: "In corso", image: "", image_note: "", description: "" };

export default function ProjectsTab({ onAuthError }) {useLanguage();
  const { projects, refreshProjects } = useSite();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setValue = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const handleError = (e) => {
    if (e.response?.status === 401) onAuthError();else
    toast.error(t("Operazione non riuscita"));
  };

  const save = async () => {
    if (!form.title.trim()) {
      toast.error(t("Il titolo è obbligatorio"));
      return;
    }
    setSaving(true);
    try {
      if (form.id) {
        await api.put(`/admin/projects/${form.id}`, form);
        toast.success(t("Lavoro aggiornato"));
      } else {
        await api.post("/admin/projects", form);
        toast.success(t("Lavoro aggiunto al sito"));
      }
      setForm(null);
      await refreshProjects();
    } catch (e) {
      handleError(e);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(t(`Eliminare "${p.title}"?`))) return;
    try {
      await api.delete(`/admin/projects/${p.id}`);
      toast.success(t("Lavoro eliminato"));
      await refreshProjects();
    } catch (e) {
      handleError(e);
    }
  };

  return (
    <div className="space-y-6" data-testid="projects-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-600">{t(projects.length)}{t(" lavori pubblicati")}</p>
        <button data-testid="project-new-button" onClick={() => setForm({ ...EMPTY })}
        className="inline-flex items-center gap-1.5 rounded-full bg-brand-navy px-5 py-2.5 text-xs font-bold text-white hover:bg-brand-navydark">
          <Plus className="h-4 w-4" />{t(" Nuovo lavoro")}
        </button>
      </div>

      {t(form &&
      <div className="rounded-2xl border-2 border-brand-lime bg-white p-6" data-testid="project-form">
          <div className="mb-5 flex items-center justify-between">
            <h3 className="font-display text-lg font-bold text-brand-navydeep">{t(form.id ? "Modifica lavoro" : "Nuovo lavoro")}</h3>
            <button data-testid="project-form-close" onClick={() => setForm(null)} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-500">{t("Titolo *")}</label>
              <input value={form.title} onChange={set("title")} data-testid="project-form-title"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand-navy" />
            </div>
            <div>
              <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-500">{t("Categoria")}</label>
              <select value={form.category} onChange={set("category")} data-testid="project-form-category"
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-navy">
                {t(CATEGORIES.map((c) => <option key={c} value={c}>{t(c)}</option>))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-500">{t("Stato")}</label>
              <select value={form.status} onChange={set("status")} data-testid="project-form-status"
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-navy">
                <option value="In corso">{t("In corso")}</option>
                <option value="Completato">{t("Completato")}</option>
                <option value="Da realizzare">{t("Da realizzare")}</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-500">{t("Località")}</label>
              <input value={form.location} onChange={set("location")} data-testid="project-form-location"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand-navy" />
            </div>
            <div>
              <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-500">{t("Anno")}</label>
              <input value={form.year} onChange={set("year")} data-testid="project-form-year"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand-navy" />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-500">{t("Immagine (URL)")}</label>
              <input value={form.image} onChange={set("image")} data-testid="project-form-image" placeholder={t("https://…")}
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand-navy" />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="project-image-note" className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-500">{t("Didascalia immagine (es. immagine illustrativa)")}</label>
              <input id="project-image-note" value={form.image_note || ""} onChange={set("image_note")} data-testid="project-form-image-note" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand-navy" />
            </div>
            <div className="sm:col-span-2">
              <div className="mb-1 flex items-center justify-between">
                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500">{t("Descrizione")}</label>
                <AiButton field={`Descrizione del lavoro "${form.title || "di cantiere"}" (${form.category})`}
              getText={() => form.description} onResult={setValue("description")}
              onAuthError={onAuthError} testid="project-form-ai-button" />
              </div>
              <textarea rows={3} value={form.description} onChange={set("description")} data-testid="project-form-description"
            className="w-full resize-none rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand-navy" />
            </div>
          </div>
          <button onClick={save} disabled={saving} data-testid="project-form-save"
        className="mt-5 rounded-full bg-brand-navy px-6 py-2.5 text-xs font-bold text-white hover:bg-brand-navydark disabled:opacity-60">
            {t(saving ? "Salvataggio…" : "Salva lavoro")}
          </button>
        </div>)
      }

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {t(projects.map((p) =>
        <div key={p.id} data-testid={`admin-project-${p.id}`} className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
            {t(p.image && <img src={p.image} alt={t(p.title)} className="h-36 w-full object-cover" />)}
            <div className="p-4">
              <p className="font-mono text-[10px] uppercase tracking-widest text-brand-limedark">{t(p.category)}</p>
              <h3 className="mt-1 font-display text-sm font-bold text-brand-navydeep">{t(p.title)}</h3>
              <p className="mt-1 text-xs text-slate-500">{t(p.location)} · {t(p.year)} · {t(p.status)}</p>
              <div className="mt-3 flex gap-2">
                <button data-testid={`admin-project-edit-${p.id}`} onClick={() => setForm({ ...p })}
              className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:border-brand-navy hover:text-brand-navy">
                  <Pencil className="h-3 w-3" />{t(" Modifica")}
              </button>
                <button data-testid={`admin-project-delete-${p.id}`} onClick={() => remove(p)}
              className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-red-500 hover:border-red-400">
                  <Trash2 className="h-3 w-3" />{t(" Elimina")}
              </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>);

}
